import React from 'react';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';

const HAS_DOLLAR = /\$[^$]+\$/;
const HAS_DOLLAR_BLOCK = /\$\$[\s\S]+?\$\$/;
const HAS_PAREN = /\\\(|\\\[/;
const HAS_BACKSLASH_CMD = /\\(?:int|iint|iiint|oint|frac|dfrac|tfrac|binom|sqrt|left|right|cdot|cdots|ldots|times|div|pm|mp|partial|nabla|infty|sum|prod|lim|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|pi|rho|sigma|tau|upsilon|phi|varphi|chi|psi|omega|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Phi|Psi|Omega|leq|geq|neq|approx|equiv|sim|to|rightarrow|leftarrow|Rightarrow|Leftarrow|mathbb|mathbf|mathrm|mathcal|mathit|vec|hat|bar|tilde|overline|underline|sin|cos|tan|cot|sec|csc|sinh|cosh|tanh|log|ln|exp|det|dim|ker|deg|gcd|lcm|min|max|arg|begin|end|text)\b/;

const hasMath = (s) =>
  typeof s === 'string' &&
  (HAS_DOLLAR.test(s) || HAS_DOLLAR_BLOCK.test(s) || HAS_PAREN.test(s) || HAS_BACKSLASH_CMD.test(s));

/**
 * Metni $$...$$ ve $...$ bloklarına ve aralarındaki düz metne ayırır.
 * Düz metin segmentinde bare \cmd varsa, sadece o LaTeX parçasını $...$ ile sarar.
 */
const prepareForLatex = (s) => {
  const parts = [];
  // $$...$$ ve $...$ bloklarını bul
  const re = /(\$\$[\s\S]+?\$\$|\$[^$\n]+?\$)/g;
  let last = 0;
  let m;

  while ((m = re.exec(s)) !== null) {
    if (m.index > last) {
      // Dolar bloğu öncesi düz metin — bare \cmd varsa o kısmı sar
      parts.push(wrapBareCmds(s.slice(last, m.index)));
    }
    // Dolar bloğunu olduğu gibi ekle
    parts.push(m[0]);
    last = m.index + m[0].length;
  }

  if (last < s.length) {
    parts.push(wrapBareCmds(s.slice(last)));
  }

  return parts.join('');
};

/**
 * Düz metin segmentinde \begin{...}...\end{...} bloklarını $$...$$,
 * diğer bare \cmd ifadelerini $...$ ile sarar.
 * Türkçe düz metne dokunmaz.
 */
const wrapBareCmds = (text) => {
  if (!HAS_BACKSLASH_CMD.test(text)) return text;

  // \begin{...}...\end{...} bloklarını $$...$$ ile sar
  if (/\\begin\{/.test(text)) {
    return text.replace(
      /(\\begin\{[a-zA-Z*]+\}[\s\S]*?\\end\{[a-zA-Z*]+\})/g,
      '$$$$$1$$$$'
    );
  }

  // Bare \cmd ifadesini bul: \cmd ve peşindeki {argüman} ve alt/üst indisler dahil
  // Örnek: \frac{a}{b}, \sqrt{x}, \int_{0}^{1} x dx
  // Strateji: \cmd ile başlayan ve sonrasında gelen { } ^ _ karakterlerini de kapsayan
  // matematiksel token grubunu $...$ ile sar; aralarındaki düz metne dokunma.
  const mathTokenRe = /\\[a-zA-Z]+(?:\{[^{}]*(?:\{[^{}]*\}[^{}]*)?\}|\[[^\]]*\]|[\^_]\{[^{}]*\}|[\^_][^\s,;.)])*(?:\s+[a-zA-Z0-9_^{}\\]+)*/g;
  return text.replace(mathTokenRe, (match) => `$${match.trim()}$`);
};

const MathText = ({ children, as: Tag = 'span', ...rest }) => {
  const text = children == null ? '' : String(children);

  if (!hasMath(text)) {
    return <Tag {...rest}>{text}</Tag>;
  }

  const prepared = prepareForLatex(text);

  return (
    <Tag {...rest}>
      <Latex>{prepared}</Latex>
    </Tag>
  );
};

export default MathText;
